/**
 * Utility functions for accessing nested object properties
 * Supports deep nesting with dot notation (e.g., 'soldTo.address.city')
 */

/**
 * Get value from nested object using dot notation path
 *
 * @param obj - Source object
 * @param path - Dot notation path (e.g., 'soldTo.name', 'shipTo.address.city')
 * @returns Value at path or undefined if not found
 *
 * @example
 * const order = { soldTo: { name: 'John', address: { city: 'NYC' } } };
 * getNestedValue(order, 'soldTo.name') // 'John'
 * getNestedValue(order, 'soldTo.address.city') // 'NYC'
 * getNestedValue(order, 'invalid.path') // undefined
 */
export function getNestedValue<T = any>(obj: any, path: string): T | undefined {
  if (!obj || !path) {
    return undefined;
  }

  // Handle array notation (e.g., 'items[0].name')
  const pathWithArrays = path.replace(/\[(\d+)\]/g, '.$1');

  const keys = pathWithArrays.split('.');
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }

    // Check if key exists
    if (!(key in current)) {
      return undefined;
    }

    current = current[key];
  }

  return current;
}

/**
 * Set value in nested object using dot notation path
 * Creates intermediate objects if they don't exist
 *
 * @param obj - Target object
 * @param path - Dot notation path
 * @param value - Value to set
 *
 * @example
 * const order = {};
 * setNestedValue(order, 'soldTo.name', 'John');
 * // order = { soldTo: { name: 'John' } }
 */
export function setNestedValue(obj: any, path: string, value: any): void {
  if (!obj || !path) {
    return;
  }

  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];

    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {};
    }

    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
}

/**
 * Check if nested path exists in object
 *
 * @param obj - Source object
 * @param path - Dot notation path
 * @returns True if path exists
 *
 * @example
 * const order = { soldTo: { name: 'John' } };
 * hasNestedValue(order, 'soldTo.name') // true
 * hasNestedValue(order, 'soldTo.age') // false
 */
export function hasNestedValue(obj: any, path: string): boolean {
  if (!obj || !path) {
    return false;
  }

  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined || !(key in current)) {
      return false;
    }
    current = current[key];
  }

  return true;
}

/**
 * Get multiple nested values at once
 *
 * @param obj - Source object
 * @param paths - Array of dot notation paths
 * @returns Array of values in same order as paths
 *
 * @example
 * const order = { soldTo: { name: 'John' }, shipTo: { name: 'Jane' } };
 * getNestedValues(order, ['soldTo.name', 'shipTo.name']) // ['John', 'Jane']
 */
export function getNestedValues(obj: any, paths: string[]): any[] {
  return paths.map(path => getNestedValue(obj, path));
}

/**
 * Delete nested value from object
 *
 * @param obj - Target object
 * @param path - Dot notation path
 * @returns True if value was deleted
 */
export function deleteNestedValue(obj: any, path: string): boolean {
  if (!obj || !path) {
    return false;
  }

  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current)) {
      return false;
    }
    current = current[key];
  }

  const lastKey = keys[keys.length - 1];
  if (lastKey in current) {
    delete current[lastKey];
    return true;
  }

  return false;
}

/**
 * Flatten object to single level with dot notation keys
 *
 * @param obj - Object to flatten
 * @param prefix - Prefix for keys (used in recursion)
 * @returns Flattened object
 *
 * @example
 * const order = { soldTo: { name: 'John', address: { city: 'NYC' } } };
 * flattenObject(order)
 * // { 'soldTo.name': 'John', 'soldTo.address.city': 'NYC' }
 */
export function flattenObject(obj: any, prefix: string = ''): Record<string, any> {
  const result: Record<string, any> = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        Object.assign(result, flattenObject(value, newKey));
      } else {
        result[newKey] = value;
      }
    }
  }

  return result;
}

/**
 * Unflatten object from dot notation keys to nested structure
 *
 * @param obj - Flattened object
 * @returns Nested object
 *
 * @example
 * const flat = { 'soldTo.name': 'John', 'soldTo.address.city': 'NYC' };
 * unflattenObject(flat)
 * // { soldTo: { name: 'John', address: { city: 'NYC' } } }
 */
export function unflattenObject(obj: Record<string, any>): any {
  const result: any = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      setNestedValue(result, key, obj[key]);
    }
  }

  return result;
}

/**
 * Get all possible paths in an object
 * Useful for debugging or autocomplete
 *
 * @param obj - Source object
 * @param prefix - Prefix for paths (used in recursion)
 * @param maxDepth - Maximum depth to traverse
 * @returns Array of all paths
 *
 * @example
 * const order = { soldTo: { name: 'John', address: { city: 'NYC' } } };
 * getAllPaths(order)
 * // ['soldTo', 'soldTo.name', 'soldTo.address', 'soldTo.address.city']
 */
export function getAllPaths(obj: any, prefix: string = '', maxDepth: number = 10): string[] {
  if (maxDepth <= 0) {
    return [];
  }

  const paths: string[] = [];

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      const newPath = prefix ? `${prefix}.${key}` : key;
      paths.push(newPath);

      if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        paths.push(...getAllPaths(value, newPath, maxDepth - 1));
      }
    }
  }

  return paths;
}

/**
 * Clone object deeply
 *
 * @param obj - Object to clone
 * @returns Deep clone of object
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as any;
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as any;
  }

  if (obj instanceof Object) {
    const clonedObj: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone((obj as any)[key]);
      }
    }
    return clonedObj;
  }

  return obj;
}

/**
 * Compare two values for equality (deep comparison for objects/arrays)
 *
 * @param a - First value
 * @param b - Second value
 * @returns True if equal
 */
export function deepEquals(a: any, b: any): boolean {
  if (a === b) {
    return true;
  }

  if (a === null || b === null || a === undefined || b === undefined) {
    return a === b;
  }

  if (typeof a !== typeof b) {
    return false;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((item, index) => deepEquals(item, b[index]));
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
      return false;
    }

    return keysA.every(key => deepEquals(a[key], b[key]));
  }

  return false;
}

/**
 * Convert a string path to array of keys
 *
 * @param path - Dot notation path
 * @returns Array of keys
 *
 * @example
 * pathToKeys('soldTo.address.city') // ['soldTo', 'address', 'city']
 */
export function pathToKeys(path: string): string[] {
  return path.split('.');
}

/**
 * Convert array of keys to string path
 *
 * @param keys - Array of keys
 * @returns Dot notation path
 *
 * @example
 * keysToPath(['soldTo', 'address', 'city']) // 'soldTo.address.city'
 */
export function keysToPath(keys: string[]): string {
  return keys.join('.');
}
